import { Injectable } from '@angular/core';
import { ConfigService } from 'src/app/service/config.service';
import { UserInfoService } from './user-info.service';
import { USER_SCOPED_KEYS } from './storage-keys';

/**
 * localStorage for per-user data, namespaced by the logged-in account's email
 * (`<name>::<email>`). This isolates one user's view state from the next on a
 * shared browser *structurally* — no logout wipe needed — while letting the same
 * user's data survive a logout/login cycle.
 *
 * Device-level preferences (theme, aside, chartVisible) deliberately do NOT go
 * through here; they stay global on raw localStorage.
 */
@Injectable({ providedIn: 'root' })
export class UserScopedStorageService {
  constructor(
    private config: ConfigService,
    private userInfo: UserInfoService,
  ) {}

  /** Namespaced key, or null when storage is off or no user is resolved. */
  private keyFor(name: string): string | null {
    if (!this.config.appConfig?.setup?.useStorage) return null;
    const email = this.userInfo.email;
    return email ? `${name}::${email}` : null;
  }

  getItem(name: string): string | null {
    const key = this.keyFor(name);
    if (!key) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  setItem(name: string, value: string): void {
    const key = this.keyFor(name);
    if (!key) return;
    try {
      localStorage.setItem(key, value);
    } catch {
      /* storage full / blocked — per-user state is best-effort */
    }
  }

  removeItem(name: string): void {
    const key = this.keyFor(name);
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  /**
   * Remove the pre-namespacing bare (global) keys once. Those were shared across
   * every account on the device; nothing reads them anymore, so drop them so no
   * account's leftover data lingers. Safe to call repeatedly (idempotent).
   */
  purgeLegacyGlobalKeys(): void {
    try {
      USER_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      /* ignore */
    }
  }
}
